import * as os from 'node:os'
import type { ComputedEntityDefinition, ModuleContext, ModuleDTO } from '@gzl10/nexus-sdk'
import { useIconField, useTextField, useSelectField, useNumberField, useCheckboxField, useTagsField, useNameField, useDescriptionField } from '@gzl10/nexus-sdk/fields'
import type { OsComputedDTO } from './system.types.js'
import { toModuleDTO } from './system.helpers.js'

/**
 * EntityDefinition for system Modules (computed).
 * Data computed at runtime from the engine.
 */
export const moduleEntity: ComputedEntityDefinition = {
  type: 'computed',
  label: { en: 'Modules', es: 'Módulos' },
  icon: 'mdi:package-variant-closed',
  labelField: 'name',
  routePrefix: '/modules',
  displayMode: 'masonry',
  availableDisplayModes: ['masonry', 'table', 'list'],
  defaultSort: { field: 'name', order: 'asc' },

  fields: {
    name: useNameField({
      size: 50,
      meta: { searchable: true, sortable: true }
    }),
    label: useTextField({
      label: { en: 'Label', es: 'Etiqueta' },
      size: 100,
      nullable: false,
      meta: { sortable: true, showInDisplay: false }
    }),
    icon: useIconField({ label: { en: 'Icon', es: 'Icono' }, size: 50, meta: { showInDisplay: false } }),
    type: {
      ...useSelectField({
        label: { en: 'Type', es: 'Tipo' },
        options: [
          { value: 'core', label: { en: 'Core', es: 'Core' } },
          { value: 'plugin', label: { en: 'Plugin', es: 'Plugin' } },
          { value: 'auth-plugin', label: { en: 'Auth Plugin', es: 'Plugin de autenticación' } },
          { value: 'custom', label: { en: 'Custom', es: 'Personalizado' } }
        ],
        nullable: false,
        meta: { sortable: true }
      }),
      validation: { enum: ['core', 'plugin', 'auth-plugin', 'custom'] }
    },
    description: useDescriptionField({
      mode: 'text',
      meta: { showInDisplay: false, searchable: true }
    }),
    routePrefix: useTextField({
      label: { en: 'Route', es: 'Ruta' },
      size: 50,
      nullable: true
    }),
    dependencies: useTagsField({
      label: { en: 'Dependencies', es: 'Dependencias' },
      nullable: true
    }),
    subjects: useTagsField({
      label: { en: 'Subjects', es: 'Sujetos' },
      nullable: true
    }),
    definitionsCount: useNumberField({
      label: { en: 'Entities', es: 'Entidades' },
      nullable: false
    }),
    hasRoutes: useCheckboxField({
      label: { en: 'Routes', es: 'Rutas' }
    }),
    hasMigrate: useCheckboxField({
      label: { en: 'Migration', es: 'Migración' },
      meta: { showInDisplay: false }
    }),
    hasInit: useCheckboxField({
      label: { en: 'Init', es: 'Inicialización' },
      meta: { showInDisplay: false }
    })
  },

  // Requiere autenticación - datos de sistema son sensibles
  casl: {
    subject: 'SystemModule',
    permissions: {
      EDITOR: { actions: ['read'] },
      USER: { actions: ['read'] },
      VIEWER: { actions: ['read'] },
      SUPPORT: { actions: ['read'] }
    }
  },

  // Función que calcula los módulos desde el engine (devuelve ModuleDTO completo)
  compute: async (ctx: ModuleContext): Promise<ModuleDTO[]> => {
    return ctx.engine.getModules().map(mod => toModuleDTO(mod, ctx))
  },

  cache: {
    ttl: 60 // Cache de 60 segundos (módulos no cambian en runtime)
  }
}

/**
 * EntityDefinition for Operating System info (computed).
 * Data computed at runtime from node:os.
 */
export const osEntity: ComputedEntityDefinition = {
  type: 'computed',
  label: { en: 'OS Info', es: 'Información del SO' },
  icon: 'mdi:server',
  labelField: 'hostname',
  routePrefix: '/os',
  isSingle: true,
  order: 1,
  refreshInterval: 5000,

  fields: {
    hostname: useTextField({
      label: { en: 'Hostname', es: 'Nombre de servidor' },
      size: 100,
      nullable: false,
      inputProps: { order: 1 }
    }),
    platform: {
      ...useSelectField({
        label: { en: 'Platform', es: 'Plataforma' },
        options: [
          { value: 'darwin', label: { en: 'macOS', es: 'macOS' } },
          { value: 'linux', label: { en: 'Linux', es: 'Linux' } },
          { value: 'win32', label: { en: 'Windows', es: 'Windows' } },
          { value: 'freebsd', label: { en: 'FreeBSD', es: 'FreeBSD' } }
        ],
        nullable: false,
        meta: { sortable: true }
      }),
      inputProps: { order: 2 }
    },
    arch: {
      ...useSelectField({
        label: { en: 'Architecture', es: 'Arquitectura' },
        options: [
          { value: 'x64', label: { en: 'x64', es: 'x64' } },
          { value: 'arm64', label: { en: 'ARM64', es: 'ARM64' } },
          { value: 'arm', label: { en: 'ARM', es: 'ARM' } },
          { value: 'ia32', label: { en: 'x86', es: 'x86' } }
        ],
        nullable: false
      }),
      inputProps: { order: 3 }
    },
    type: {
      ...useTextField({
        label: { en: 'Type', es: 'Tipo' },
        size: 30,
        nullable: false
      }),
      inputProps: { order: 4 }
    },
    release: {
      ...useTextField({
        label: { en: 'Release', es: 'Versión' },
        size: 50,
        nullable: false
      }),
      inputProps: { order: 5 }
    },
    uptime: {
      ...useNumberField({
        label: { en: 'Uptime', es: 'Tiempo activo' },
        nullable: false,
        meta: { sortable: true }
      }),
      inputProps: { order: 6, format: 'duration' }
    },
    nexusUptime: {
      ...useNumberField({
        label: { en: 'Nexus Uptime', es: 'Tiempo activo de Nexus' },
        nullable: false
      }),
      inputProps: { order: 7, format: 'duration' }
    },
    cpuModel: {
      ...useTextField({
        label: { en: 'CPU Model', es: 'Modelo de CPU' },
        size: 100,
        nullable: false
      }),
      inputProps: { order: 8, span: 2 }
    },
    cpuCount: {
      ...useNumberField({
        label: { en: 'CPU Cores', es: 'Núcleos de CPU' },
        nullable: false
      }),
      inputProps: { order: 9 }
    },
    totalMemory: {
      ...useNumberField({
        label: { en: 'Total Memory', es: 'Memoria total' },
        nullable: false
      }),
      inputProps: { order: 10, format: 'bytes' }
    },
    freeMemory: {
      ...useNumberField({
        label: { en: 'Free Memory', es: 'Memoria libre' },
        nullable: false
      }),
      inputProps: { order: 11, format: 'bytes' }
    },
    loadAverage: {
      label: { en: 'Load Average (1m / 5m / 15m)', es: 'Promedio de carga (1m / 5m / 15m)' },
      input: 'text',
      db: { type: 'json', nullable: false },
      inputProps: { order: 12, format: 'loadAverage', span: 2 }
    }
  },

  // Info técnica del sistema - para soporte y auditoría
  casl: {
    subject: 'SystemOsInfo',
    permissions: {
      SUPPORT: { actions: ['read'] }
    }
  },

  compute: async (_ctx: ModuleContext): Promise<OsComputedDTO[]> => {
    const cpus = os.cpus()
    return [{
      hostname: os.hostname(),
      platform: os.platform(),
      arch: os.arch(),
      release: os.release(),
      type: os.type(),
      uptime: os.uptime(),
      nexusUptime: Math.floor(process.uptime()),
      totalMemory: os.totalmem(),
      freeMemory: os.freemem(),
      cpuCount: cpus.length,
      cpuModel: cpus[0]?.model ?? 'Unknown',
      loadAverage: os.loadavg()
    }]
  },

  // No cache — refreshInterval handles polling frequency from the frontend
}
